import React, { RefObject } from 'react';
import styled from 'styled-components';
import * as d3 from 'd3';

import ArrowMarker from './ArrowMarker';
import generateId from 'src/lib/generateId';
import { relativeThreadBlockHiddenCss, relativeThreadBlockVisibleCss } from '../Layout';

type FromTo = {
  connectionPoint: 'top' | 'bottom' | 'left' | 'right';
  rect: DOMRect;
};

const DELTA_TOP = 14;

const SvgLine = ({
  from,
  to,
  shiftX,
  shiftY,
  hideLines,
  scrollableRef,
}: {
  from: FromTo;
  to: FromTo;
  shiftX: number;
  shiftY: number;
  hideLines: boolean;
  scrollableRef: RefObject<HTMLDivElement>;
}) => {
  const [fromPosition, toPosition] = (() => {
    if (!scrollableRef.current) {
      return [
        { x: 0, y: 0 },
        { x: 0, y: 0 },
      ];
    }

    const scrollTop = 0 + shiftY;
    const scrollLeft = 0 + shiftX;

    return [
      computeConnectionPoint(from.rect, from.connectionPoint, scrollTop, scrollLeft),
      computeConnectionPoint(to.rect, to.connectionPoint, scrollTop, scrollLeft),
    ];
  })();

  const id = generateId();

  if (fromPosition.x === 0 || fromPosition.y === 0) return null;
  if (toPosition.x === 0 || toPosition.y === 0) return null;

  const dir = `${from.connectionPoint}-${to.connectionPoint}`;

  const path = d3.path();
  path.moveTo(fromPosition.x, fromPosition.y);

  let line = '';
  switch (dir) {
    case 'bottom-top':
      toPosition.y -= DELTA_TOP;
      line = `M${fromPosition.x},${fromPosition.y} 
        L${fromPosition.x},${toPosition.y - 60 - fromPosition.y > 0 ? toPosition.y - 60 : fromPosition.y}
        C${fromPosition.x},${toPosition.y - 0} ${toPosition.x},${toPosition.y - 25} ${toPosition.x},${toPosition.y - 10}
        L${toPosition.x},${toPosition.y}
        `;
      break;
    case 'top-bottom':
      break;
    case 'left-right':
      break;
    case 'right-left':
      const dx = toPosition.x - fromPosition.x;
      const k = dx / 3;

      path.bezierCurveTo(
        toPosition.x - k,
        fromPosition.y,
        fromPosition.x,
        toPosition.y,
        toPosition.x - k,
        toPosition.y
      );
      path.lineTo(toPosition.x, toPosition.y);

      line = path.toString();

      break;
  }

  return (
    <>
      <defs>
        <ArrowMarker id={id} size={10} />
      </defs>

      <StyledPath d={line} markerEnd={`url(#${id})`} stroke="black" fill="transparent" hideLines={hideLines} />
    </>
  );
};

const computeConnectionPoint = (
  rect: DOMRect,
  connectionPoint: 'top' | 'bottom' | 'left' | 'right',
  scrollTop: number = 0,
  scrollLeft: number = 0
) => {
  switch (connectionPoint) {
    case 'top':
      return { x: rect.left + rect.width / 2 - scrollLeft, y: rect.top - scrollTop };
    case 'bottom':
      return { x: rect.left + rect.width / 2 - scrollLeft, y: rect.top + rect.height - scrollTop };
    case 'left':
      return { x: rect.left - scrollLeft, y: rect.top + rect.height / 2 - scrollTop };
    case 'right':
      return { x: rect.left + rect.width - scrollLeft, y: rect.top + rect.height / 2 - scrollTop };
  }
};

const StyledPath = styled.path`
  fill: none;
  stroke: ${p => p.theme.colors.gray500};

  ${p => (p.hideLines ? relativeThreadBlockHiddenCss : relativeThreadBlockVisibleCss)}
`;

export default SvgLine;
